import { useEffect, useState } from "react";
import { Button, Input, Modal, DialogFooter, Loader } from "@/ui/atoms";
import { FormField } from "@/ui/molecules";

export type TAccountDraft = {
    account_code: string;
    account_name: string;
    is_prohibited: boolean;
};

export type TChartOfAccountsFormModalProps = {
    open: boolean;
    mode: "create" | "edit";
    initial?: TAccountDraft;
    isSaving?: boolean;
    error?: string | null;
    onSubmit: (draft: TAccountDraft) => void;
    onClose: () => void;
};

const emptyDraft = (): TAccountDraft => ({
    account_code: "",
    account_name: "",
    is_prohibited: false,
});

export function ChartOfAccountsFormModal({
    open,
    mode,
    initial,
    isSaving = false,
    error,
    onSubmit,
    onClose,
}: TChartOfAccountsFormModalProps) {
    const [draft, setDraft] = useState<TAccountDraft>(initial ?? emptyDraft());

    // Re-seed the form whenever the modal (re)opens for a different row.
    useEffect(() => {
        if (open) setDraft(initial ?? emptyDraft());
    }, [open, initial]);

    const isValid = draft.account_code.trim() !== "" && draft.account_name.trim() !== "";

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!isValid) return;
        onSubmit({
            account_code: draft.account_code.trim(),
            account_name: draft.account_name.trim(),
            is_prohibited: draft.is_prohibited,
        });
    };

    return (
        <Modal
            open={open}
            onOpenChange={(next) => !next && onClose()}
            title={mode === "create" ? "Add Account" : "Edit Account"}
            description="Account code, name, and prohibited flag."
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                        {error}
                    </div>
                )}
                <FormField label="Account name" htmlFor="coa-account-name" required>
                    <Input
                        id="coa-account-name"
                        aria-label="Account name"
                        value={draft.account_name}
                        onChange={(e) => setDraft({ ...draft, account_name: e.target.value })}
                        placeholder="Account name"
                    />
                </FormField>
                <FormField label="Account code" htmlFor="coa-account-code" required>
                    <Input
                        id="coa-account-code"
                        aria-label="Account code"
                        value={draft.account_code}
                        onChange={(e) => setDraft({ ...draft, account_code: e.target.value })}
                        placeholder="Account code"
                    />
                </FormField>
                <label className="inline-flex items-center gap-2">
                    <input
                        type="checkbox"
                        aria-label="Prohibited"
                        checked={draft.is_prohibited}
                        onChange={(e) => setDraft({ ...draft, is_prohibited: e.target.checked })}
                    />
                    <span>Prohibited</span>
                </label>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSaving || !isValid}>
                        {isSaving ? (
                            <>
                                <Loader size="sm" className="mr-2" />
                                Saving...
                            </>
                        ) : mode === "create" ? (
                            "Create"
                        ) : (
                            "Save"
                        )}
                    </Button>
                </DialogFooter>
            </form>
        </Modal>
    );
}
